import { useEffect, useMemo, useState } from "react";
import { Bike, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/brand";

const INITIALS = (name) =>
  name
    .replace(/[^a-zA-Z0-9 ]/g, "")
    .split(" ")
    .filter(Boolean)
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "CJ";

function OfficerCard({ officer, idx, groupKey }) {
  return (
    <article
      className="group relative bg-[#0a0a0a] border border-white/10 hover:border-brand/60 transition-all duration-500 overflow-hidden"
      data-testid={`officer-card-${groupKey}-${idx}`}
    >
      <div className="aspect-[3/4] relative overflow-hidden bg-gradient-to-br from-[#1a0a14] via-[#0a0a0a] to-[#0a0a0a] flex items-center justify-center">
        {officer.image_url ? (
          <>
            <img
              src={officer.image_url}
              alt={`${officer.name} — ${officer.title}`}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-[1200ms]"
              data-testid={`officer-image-${groupKey}-${idx}`}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-transparent opacity-70 group-hover:opacity-50 transition-opacity" />
          </>
        ) : (
          <>
            <div
              className="absolute inset-0 opacity-30 group-hover:opacity-60 transition-opacity"
              style={{
                background:
                  "radial-gradient(circle at 50% 30%, rgba(233,30,140,0.45) 0%, transparent 60%)",
              }}
            />
            <span className="font-serif text-6xl text-brand/70 group-hover:text-brand transition-colors relative">
              {INITIALS(officer.name)}
            </span>
          </>
        )}
        <img
          src={BRAND.logoImage}
          alt=""
          aria-hidden="true"
          className="absolute top-3 right-3 w-7 h-auto opacity-80 drop-shadow-[0_0_6px_rgba(0,0,0,0.9)]"
        />
      </div>
      <div className="p-5 space-y-1">
        <h3
          className="font-serif text-xl text-white truncate"
          data-testid={`officer-name-${groupKey}-${idx}`}
          title={officer.name}
        >
          {officer.name}
        </h3>
        <p className="text-brand text-[11px] uppercase tracking-[0.22em] font-sans">
          {officer.title}
        </p>
        {officer.quote && (
          <p
            className="text-white/60 text-xs italic leading-relaxed pt-3 border-t border-white/10 mt-3"
            data-testid={`officer-quote-${groupKey}-${idx}`}
          >
            "{officer.quote}"
          </p>
        )}
      </div>
    </article>
  );
}

function OfficerGroup({ icon: Icon, label, est, officers, groupKey }) {
  if (!officers.length) return null;
  return (
    <div className="space-y-8" data-testid={`officer-group-${groupKey}`}>
      <div className="flex flex-wrap items-end justify-between gap-4 pb-4 border-b border-white/10">
        <div className="flex items-center gap-4">
          <span className="inline-flex items-center justify-center w-12 h-12 border border-brand/50 bg-[#0a0a0a]">
            <Icon className="w-5 h-5 text-brand" strokeWidth={1.5} />
          </span>
          <div>
            <div className="text-[10px] uppercase tracking-[0.35em] text-brand">
              {label.split(" ")[0]}
            </div>
            <h3 className="font-serif text-2xl sm:text-3xl text-white">
              {label}
            </h3>
          </div>
        </div>
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/55 border border-white/15 px-3 py-1.5">
          Est. {est}
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
        {officers.map((o, i) => (
          <OfficerCard key={o.id} officer={o} idx={i} groupKey={groupKey} />
        ))}
      </div>
    </div>
  );
}

export default function Officers() {
  const [officers, setOfficers] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.get("/officers"), api.get("/members")])
      .then(([oRes, mRes]) => {
        if (cancelled) return;
        setOfficers(oRes.data || []);
        setMembers(mRes.data || []);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const { mcOfficers, scOfficers } = useMemo(() => {
    const mcOfficers = officers.filter((o) => o.branch === "MC");
    const scOfficers = officers.filter((o) => o.branch === "SC");
    return { mcOfficers, scOfficers };
  }, [officers]);

  return (
    <section
      id="officers"
      className="relative py-24 sm:py-32 border-t border-white/5"
      data-testid="officers-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 mb-20">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.35em] text-brand mb-4 flex items-center gap-3">
              <span className="h-px w-10 bg-brand" /> The Board & The Sisters
            </div>
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl font-serif leading-[1.02]"
              data-testid="officers-title"
            >
              Meet the
              <br />
              <span className="italic text-brand">Crowned Queens.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-6">
            <p className="text-lg text-white/65 leading-relaxed max-w-xl">
              Every piece of our story is written by the women who lead it.
              Below are the executive boards of our Motorcycle Club and Social
              Club — and the sisters who make the crown shine.
            </p>
          </div>
        </div>

        {loading ? (
          <div
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5"
            data-testid="officers-loading"
          >
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="aspect-[3/4] bg-white/5 animate-pulse"
                data-testid={`officer-skeleton-${i}`}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-20">
            <OfficerGroup
              icon={Bike}
              label="Motorcycle Club"
              est={2020}
              officers={mcOfficers}
              groupKey="mc"
            />
            <OfficerGroup
              icon={Sparkles}
              label="Social Club"
              est={2021}
              officers={scOfficers}
              groupKey="sc"
            />

            {/* Sisters of the Crown — SC members strip */}
            {members.length > 0 && (
              <div className="space-y-8" data-testid="members-group">
                <div className="flex flex-wrap items-end justify-between gap-4 pb-4 border-b border-white/10">
                  <div className="flex items-center gap-4">
                    <img
                      src={BRAND.logoImage}
                      alt=""
                      aria-hidden="true"
                      className="h-12 w-auto drop-shadow-[0_0_12px_rgba(233,30,140,0.4)]"
                    />
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.35em] text-brand">
                        Social Club
                      </div>
                      <h3 className="font-serif text-2xl sm:text-3xl text-white">
                        Sisters of the Crown
                      </h3>
                    </div>
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.3em] text-white/55 border border-white/15 px-3 py-1.5">
                    {members.length} SC Queens
                  </span>
                </div>

                <div
                  className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3"
                  data-testid="members-grid"
                >
                  {members.map((m, i) => (
                    <div
                      key={m.id}
                      className="group relative bg-[#0a0a0a] border border-white/10 hover:border-brand/60 transition-all duration-500 overflow-hidden"
                      data-testid={`member-card-${i}`}
                    >
                      <div className="aspect-[3/4] relative overflow-hidden bg-[#050505] flex items-center justify-center">
                        {m.image_url ? (
                          <>
                            <img
                              src={m.image_url}
                              alt={m.name}
                              loading="lazy"
                              className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-[1200ms]"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-transparent opacity-70 group-hover:opacity-50 transition-opacity" />
                          </>
                        ) : (
                          <span className="font-serif text-brand/80 text-3xl">
                            {INITIALS(m.name)}
                          </span>
                        )}
                      </div>
                      <div className="absolute bottom-0 inset-x-0 p-3 text-center">
                        <div
                          className="font-sans text-xs sm:text-sm text-white truncate"
                          title={m.name}
                          data-testid={`member-name-${i}`}
                        >
                          {m.name}
                        </div>
                      </div>
                      <span
                        className="absolute bottom-0 left-1/2 -translate-x-1/2 h-px w-0 bg-brand group-hover:w-full transition-all duration-700"
                      />
                    </div>
                  ))}
                </div>

                <p className="font-script text-2xl text-brand pt-2">
                  One crown. Many queens.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
